import { FC, useState, useMemo, useEffect } from 'react'
import { RetroWindow, RetroButton, PixelText } from '@/components/retro'
import { useGame } from '@/providers'
import { useGameStore } from '@/store'
import { searchTokens, type TokenData } from '@/game/tokens'
import { getRealMarketContext } from '@/services/marketData'
import { TradeConfirmModal } from './TradeConfirmModal'
import type { RealMarketData } from '@/types'

interface TradingPanelProps {
  onTradeComplete?: () => void
}

// Represents an asset the user can trade from (holdings)
interface TradableAsset {
  symbol: string
  name: string
  amount: number
  price: number
  isStable: boolean
}

export const TradingPanel: FC<TradingPanelProps> = ({ onTradeComplete }) => {
  const { session } = useGameStore()
  const { buyToken, sellToken, getAvailableTokens } = useGame()

  const [fromAsset, setFromAsset] = useState<string>('USDC')
  const [showFromDropdown, setShowFromDropdown] = useState(false)
  const [searchQuery, setSearchQuery] = useState('')
  const [selectedToken, setSelectedToken] = useState<TokenData | null>(null)
  const [amount, setAmount] = useState('')
  const [isTrading, setIsTrading] = useState(false)
  const [tradeMessage, setTradeMessage] = useState<{ type: 'success' | 'error'; text: string; quote: string } | null>(null)
  const [realMarketData, setRealMarketData] = useState<RealMarketData | null>(null)
  const [showConfirmModal, setShowConfirmModal] = useState(false)
  const [pendingTrade, setPendingTrade] = useState<{
    type: 'buy' | 'sell' | 'swap'
    fromSymbol: string
    toSymbol: string
    amount: number
    estimatedOutput: number
  } | null>(null)

  const availableTokens = getAvailableTokens()

  // Fetch real market data on mount and periodically
  useEffect(() => {
    const fetchData = async () => {
      try {
        const data = await getRealMarketContext()
        setRealMarketData(data)
      } catch (err) {
        console.error('Failed to fetch real market data:', err)
      }
    }

    fetchData()

    // Refresh every 5 minutes
    const interval = setInterval(fetchData, 5 * 60 * 1000)
    return () => clearInterval(interval)
  }, [])

  // Get all tradable assets from portfolio (what user can trade FROM)
  const tradableAssets = useMemo((): TradableAsset[] => {
    if (!session) return []

    return session.player.portfolio.holdings
      .filter(h => h.amount > 0)
      .map(h => ({
        symbol: h.symbol,
        name: h.symbol === 'USDC' ? 'USD Coin' : (availableTokens.find(t => t.symbol === h.symbol)?.name || h.symbol),
        amount: h.amount,
        price: h.currentPrice,
        isStable: h.symbol === 'USDC',
      }))
      .sort((a, b) => {
        // USDC first, then by value
        if (a.isStable) return -1
        if (b.isStable) return 1
        return (b.amount * b.price) - (a.amount * a.price)
      })
  }, [session?.player.portfolio.holdings, availableTokens])

  // Get the current "from" asset details
  const currentFromAsset = tradableAssets.find(a => a.symbol === fromAsset)

  // Filter tokens based on search (exclude the "from" asset)
  const filteredTokens = useMemo(() => {
    let tokens = searchQuery
      ? searchTokens(searchQuery)
      : availableTokens.slice(0, 20)

    // Exclude USDC and the current "from" asset from the "to" list
    return tokens.filter(t => t.symbol !== 'USDC' && t.symbol !== fromAsset)
  }, [searchQuery, availableTokens, fromAsset])

  // Get current price from real market data or fall back to session market
  const getRealPrice = (symbol: string): { price: number; change: number } => {
    // USDC is always $1
    if (symbol === 'USDC') return { price: 1, change: 0 }

    // Check real market data first
    if (realMarketData) {
      if (symbol === 'BTC') return { price: realMarketData.btcPrice, change: 0 }
      if (symbol === 'ETH') return { price: realMarketData.ethPrice, change: 0 }
      if (symbol === 'SOL') return { price: realMarketData.solPrice, change: 0 }

      const gainer = realMarketData.topGainers.find(t => t.symbol === symbol)
      if (gainer) return { price: gainer.price, change: gainer.change24h }

      const loser = realMarketData.topLosers.find(t => t.symbol === symbol)
      if (loser) return { price: loser.price, change: loser.change24h }
    }

    // Fall back to session market data
    const sessionPrice = session?.market.prices[symbol]
    return {
      price: sessionPrice?.price || 0,
      change: sessionPrice?.change24h || 0,
    }
  }

  const fromPrice = getRealPrice(fromAsset).price
  const toPrice = selectedToken ? getRealPrice(selectedToken.symbol).price || selectedToken.basePrice : 0
  const toPriceChange = selectedToken ? getRealPrice(selectedToken.symbol).change : 0

  // Determine if this is a buy (from USDC), sell (to USDC), or swap (token to token)
  const tradeType: 'buy' | 'sell' | 'swap' = useMemo(() => {
    if (fromAsset === 'USDC') return 'buy'
    if (!selectedToken) return 'sell'
    return 'swap'
  }, [fromAsset, selectedToken])

  // Calculate trade value as percentage of portfolio
  const getTradeValuePercent = () => {
    if (!amount || !session) return 0
    const numAmount = parseFloat(amount)
    if (isNaN(numAmount) || numAmount <= 0) return 0

    let tradeValueUsd = 0
    if (tradeType === 'buy') {
      tradeValueUsd = numAmount // USDC amount
    } else {
      tradeValueUsd = numAmount * fromPrice // Token amount * price
    }

    return (tradeValueUsd / session.player.portfolio.totalValue) * 100
  }

  const handleTradeClick = () => {
    if (!amount || parseFloat(amount) <= 0) return
    if (tradeType !== 'sell' && !selectedToken) return

    const tradePercent = getTradeValuePercent()
    const numAmount = parseFloat(amount)

    // Show confirmation for large trades (>50% of portfolio)
    if (tradePercent >= 50) {
      const toSymbol = tradeType === 'sell' ? 'USDC' : selectedToken!.symbol
      setPendingTrade({
        type: tradeType,
        fromSymbol: fromAsset,
        toSymbol,
        amount: numAmount,
        estimatedOutput,
      })
      setShowConfirmModal(true)
      return
    }

    // Otherwise, execute directly
    executeTrade()
  }

  const handleConfirmTrade = () => {
    setShowConfirmModal(false)
    setPendingTrade(null)
    executeTrade()
  }

  const handleCancelTrade = () => {
    setShowConfirmModal(false)
    setPendingTrade(null)
  }

  const executeTrade = async () => {
    if (!amount || parseFloat(amount) <= 0) return
    if (tradeType !== 'sell' && !selectedToken) return

    setIsTrading(true)
    setTradeMessage(null)

    try {
      const numAmount = parseFloat(amount)

      if (tradeType === 'buy') {
        // Buying token with USDC
        const result = await buyToken(selectedToken!.symbol, numAmount)
        setTradeMessage({
          type: result.success ? 'success' : 'error',
          text: result.message,
          quote: result.degenQuote,
        })
        if (result.success) {
          setAmount('')
          onTradeComplete?.()
        }
      } else if (tradeType === 'sell') {
        // Selling token for USDC
        const result = await sellToken(fromAsset, numAmount)
        setTradeMessage({
          type: result.success ? 'success' : 'error',
          text: result.message,
          quote: result.degenQuote,
        })
        if (result.success) {
          setAmount('')
          onTradeComplete?.()
        }
      } else {
        // Swap: sell fromAsset for USDC, then buy selectedToken
        // First sell
        const sellResult = await sellToken(fromAsset, numAmount)
        if (!sellResult.success) {
          setTradeMessage({
            type: 'error',
            text: sellResult.message,
            quote: sellResult.degenQuote,
          })
          return
        }

        // Use the ACTUAL USDC received from the sell, not the estimated amount
        // This is critical - the trade may have slippage or different execution price
        const usdcReceived = sellResult.trade?.toAmount ?? 0

        if (usdcReceived <= 0) {
          setTradeMessage({
            type: 'error',
            text: 'Sell completed but received no USDC',
            quote: 'Something went wrong with the swap. The blockchain gods are confused.',
          })
          return
        }

        // Buy the target token with the actual USDC we received
        const buyResult = await buyToken(selectedToken!.symbol, usdcReceived)

        // Use actual amounts from both trades for accurate display
        const actualTokenReceived = buyResult.trade?.toAmount ?? 0

        setTradeMessage({
          type: buyResult.success ? 'success' : 'error',
          text: buyResult.success
            ? `Swapped ${numAmount < 1 ? numAmount.toFixed(6) : numAmount.toFixed(4)} ${fromAsset} → ${actualTokenReceived < 1 ? actualTokenReceived.toFixed(6) : actualTokenReceived.toFixed(4)} ${selectedToken!.symbol}`
            : buyResult.message,
          quote: buyResult.degenQuote,
        })
        if (buyResult.success) {
          setAmount('')
          onTradeComplete?.()
        }
      }
    } catch (err) {
      setTradeMessage({
        type: 'error',
        text: 'Trade failed',
        quote: 'The blockchain gods have rejected your offering.',
      })
    } finally {
      setIsTrading(false)
    }
  }

  const handleMaxClick = () => {
    if (currentFromAsset) {
      setAmount(currentFromAsset.amount.toString())
    }
  }

  const handlePercentClick = (percent: number) => {
    if (currentFromAsset) {
      setAmount((currentFromAsset.amount * percent / 100).toString())
    }
  }

  // Calculate estimated output
  const estimatedOutput = useMemo(() => {
    if (!amount || parseFloat(amount) <= 0) return 0
    const numAmount = parseFloat(amount)

    if (tradeType === 'buy' && selectedToken) {
      // Buying: USDC amount → token amount
      return numAmount / toPrice
    } else if (tradeType === 'sell') {
      // Selling: token amount → USDC amount
      return numAmount * fromPrice
    } else if (tradeType === 'swap' && selectedToken) {
      // Swap: from token → USDC → to token
      const usdcValue = numAmount * fromPrice
      return usdcValue / toPrice
    }
    return 0
  }, [amount, fromPrice, toPrice, tradeType, selectedToken])

  return (
    <RetroWindow title="TRADING TERMINAL" className="h-full">
      <div className="flex flex-col h-full space-y-3">
        {/* From Asset Selector */}
        <div>
          <div className="flex items-center justify-between mb-1">
            <span className="font-retro text-xs text-game-muted">FROM</span>
            {realMarketData && (
              <span className="font-mono text-[10px] text-game-profit px-2 py-0.5 border border-game-profit/30 bg-game-profit/10" title="Live data from CoinGecko">
                LIVE
              </span>
            )}
          </div>
          <div className="relative">
            <button
              onClick={() => setShowFromDropdown(!showFromDropdown)}
              className="w-full px-3 py-2 bg-game-panel border-2 border-game-border hover:border-game-accent flex items-center justify-between transition-colors"
            >
              <div className="flex items-center gap-2">
                <span className="font-pixel text-sm text-game-accent">
                  {fromAsset}
                </span>
                <span className="font-retro text-xs text-game-muted">
                  {currentFromAsset?.name || ''}
                </span>
              </div>
              <div className="flex items-center gap-2">
                <span className="font-mono text-xs text-game-text">
                  {currentFromAsset
                    ? currentFromAsset.isStable
                      ? `$${currentFromAsset.amount.toFixed(2)}`
                      : `${currentFromAsset.amount < 1 ? currentFromAsset.amount.toFixed(4) : currentFromAsset.amount.toFixed(2)}`
                    : '0'}
                </span>
                <span className="text-game-muted">▼</span>
              </div>
            </button>

            {/* From Asset Dropdown */}
            {showFromDropdown && (
              <>
                <div
                  className="fixed inset-0 z-40"
                  onClick={() => setShowFromDropdown(false)}
                />
                <div className="absolute top-full left-0 right-0 z-50 mt-1 bg-game-panel border-2 border-game-border max-h-[200px] overflow-y-auto">
                  {tradableAssets.map((asset) => (
                    <button
                      key={asset.symbol}
                      className={`w-full px-3 py-2 flex items-center justify-between hover:bg-game-accent/20 transition-colors ${
                        asset.symbol === fromAsset ? 'bg-game-accent/30' : ''
                      }`}
                      onClick={() => {
                        setFromAsset(asset.symbol)
                        setShowFromDropdown(false)
                        // Clear selected token if it's the same as from
                        if (selectedToken?.symbol === asset.symbol) {
                          setSelectedToken(null)
                        }
                      }}
                    >
                      <div className="flex items-center gap-2">
                        <span className="font-pixel text-xs text-game-accent">{asset.symbol}</span>
                        <span className="font-retro text-xs text-game-muted">{asset.name}</span>
                      </div>
                      <div className="text-right">
                        <div className="font-mono text-xs text-game-text">
                          {asset.isStable
                            ? `$${asset.amount.toFixed(2)}`
                            : asset.amount < 1 ? asset.amount.toFixed(4) : asset.amount.toFixed(2)}
                        </div>
                        {!asset.isStable && (
                          <div className="font-mono text-xs text-game-muted">
                            ≈${(asset.amount * asset.price).toFixed(2)}
                          </div>
                        )}
                      </div>
                    </button>
                  ))}
                  {tradableAssets.length === 0 && (
                    <div className="px-3 py-2 text-center">
                      <span className="font-retro text-xs text-game-muted">No assets to trade</span>
                    </div>
                  )}
                </div>
              </>
            )}
          </div>
        </div>

        {/* Swap Arrow */}
        <div className="flex justify-center">
          <span className="font-pixel text-lg text-game-muted">↓</span>
        </div>

        {/* To Token - Search and Select */}
        <div>
          <span className="font-retro text-xs text-game-muted mb-1 block">
            {fromAsset === 'USDC' ? 'BUY TOKEN' : 'SWAP TO (or leave empty to sell for USDC)'}
          </span>
          <input
            type="text"
            placeholder="Search tokens..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full bg-game-panel border-2 border-game-border px-3 py-2 font-retro text-sm text-game-text placeholder-game-muted focus:border-game-accent outline-none"
          />
        </div>

        {/* Token List */}
        <div className="flex-1 overflow-y-auto border-2 border-game-border bg-game-panel min-h-[100px] max-h-[140px]">
          {/* Sell to USDC option (only when from is not USDC) */}
          {fromAsset !== 'USDC' && (
            <button
              className={`w-full px-2 py-1.5 flex items-center justify-between border-b-2 border-game-accent/50 hover:bg-game-accent/20 transition-colors ${
                !selectedToken ? 'bg-game-accent/30' : ''
              }`}
              onClick={() => setSelectedToken(null)}
            >
              <div className="flex items-center gap-2">
                <span className="font-pixel text-xs text-game-profit">USDC</span>
                <span className="font-retro text-xs text-game-muted">Sell for cash</span>
              </div>
              <span className="font-mono text-xs text-game-profit">$1.00</span>
            </button>
          )}

          {filteredTokens.map((token) => {
            const realPriceData = getRealPrice(token.symbol)
            const price = realPriceData.price || token.basePrice
            const change = realPriceData.change
            const isSelected = selectedToken?.symbol === token.symbol
            const holding = session?.player.portfolio.holdings.find(h => h.symbol === token.symbol)

            return (
              <button
                key={token.symbol}
                className={`w-full px-2 py-1.5 flex items-center justify-between border-b border-game-border/50 hover:bg-game-accent/20 transition-colors ${
                  isSelected ? 'bg-game-accent/30' : ''
                }`}
                onClick={() => setSelectedToken(token)}
              >
                <div className="flex items-center gap-2">
                  <span className="font-pixel text-xs text-game-accent">{token.symbol}</span>
                  <span className="font-retro text-xs text-game-muted truncate max-w-[80px]">
                    {token.name}
                  </span>
                </div>
                <div className="text-right">
                  <div className="font-mono text-xs text-game-text">
                    ${price < 0.01 ? price.toFixed(6) : price.toFixed(2)}
                  </div>
                  <div className={`font-mono text-xs ${change >= 0 ? 'text-game-profit' : 'text-game-loss'}`}>
                    {change >= 0 ? '+' : ''}{change.toFixed(1)}%
                  </div>
                  {holding && (
                    <div className="font-mono text-xs text-game-gold">
                      Own: {holding.amount < 1 ? holding.amount.toFixed(4) : holding.amount.toFixed(2)}
                    </div>
                  )}
                </div>
              </button>
            )
          })}
        </div>

        {/* Selected Token Info */}
        {selectedToken && (
          <div className="p-2 border-2 border-game-accent bg-game-accent/10">
            <div className="flex justify-between items-center">
              <div>
                <PixelText size="sm" color="accent">{selectedToken.symbol}</PixelText>
                <p className="font-retro text-xs text-game-muted">{selectedToken.name}</p>
              </div>
              <div className="text-right">
                <div className="font-mono text-sm text-game-text">
                  ${toPrice < 0.01 ? toPrice.toFixed(6) : toPrice.toFixed(2)}
                </div>
                <div className={`font-mono text-xs ${toPriceChange >= 0 ? 'text-game-profit' : 'text-game-loss'}`}>
                  {toPriceChange >= 0 ? '+' : ''}{toPriceChange.toFixed(2)}%
                </div>
              </div>
            </div>
            <p className="font-retro text-xs text-game-muted mt-1 italic">
              {selectedToken.lore}
            </p>
          </div>
        )}

        {/* Amount Input */}
        <div>
          <div className="flex justify-between items-center mb-1">
            <span className="font-retro text-xs text-game-muted">
              {fromAsset} Amount
            </span>
            <span className="font-mono text-xs text-game-text">
              Balance: {currentFromAsset
                ? currentFromAsset.isStable
                  ? `$${currentFromAsset.amount.toFixed(2)}`
                  : `${currentFromAsset.amount < 1 ? currentFromAsset.amount.toFixed(4) : currentFromAsset.amount.toFixed(2)}`
                : '0'}
            </span>
          </div>
          <div className="flex gap-2">
            <input
              type="number"
              placeholder="0.00"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              className="flex-1 bg-game-panel border-2 border-game-border px-3 py-2 font-mono text-sm text-game-text placeholder-game-muted focus:border-game-accent outline-none"
            />
            <button
              onClick={handleMaxClick}
              className="px-2 py-1 border-2 border-game-border bg-game-panel hover:bg-game-accent/20 font-pixel text-xs"
            >
              MAX
            </button>
          </div>

          {/* Quick percentage buttons */}
          <div className="flex gap-1 mt-2">
            {[25, 50, 75, 100].map((percent) => (
              <button
                key={percent}
                onClick={() => handlePercentClick(percent)}
                className="flex-1 py-1 border border-game-border bg-game-panel hover:bg-game-accent/20 font-retro text-xs"
              >
                {percent}%
              </button>
            ))}
          </div>
        </div>

        {/* Estimated Output / Swap Preview */}
        {amount && parseFloat(amount) > 0 && (tradeType === 'sell' || selectedToken) && (
          <div className="p-2 border-2 border-game-accent/50 bg-game-accent/10">
            <div className="font-pixel text-xs text-game-accent mb-2">TRADE PREVIEW</div>

            <div className="space-y-1">
              {/* What you're sending */}
              <div className="flex justify-between items-center">
                <span className="font-retro text-xs text-game-muted">Sending:</span>
                <span className="font-mono text-sm text-game-loss">
                  -{parseFloat(amount).toLocaleString(undefined, { maximumFractionDigits: 4 })} {fromAsset}
                </span>
              </div>

              {/* Swap arrow */}
              <div className="text-center text-game-muted text-xs">↓</div>

              {/* What you're receiving */}
              <div className="flex justify-between items-center">
                <span className="font-retro text-xs text-game-muted">Receiving (est.):</span>
                <span className="font-mono text-sm text-game-profit font-bold">
                  +{tradeType === 'sell'
                    ? `$${estimatedOutput.toLocaleString(undefined, { maximumFractionDigits: 2 })} USDC`
                    : `${estimatedOutput.toLocaleString(undefined, { maximumFractionDigits: 4 })} ${selectedToken?.symbol}`
                  }
                </span>
              </div>

              {/* Rate */}
              <div className="flex justify-between items-center pt-1 border-t border-game-border/50">
                <span className="font-retro text-xs text-game-muted">Rate:</span>
                <span className="font-mono text-xs text-game-text">
                  {tradeType === 'buy'
                    ? `1 ${selectedToken?.symbol} = $${toPrice.toLocaleString(undefined, { maximumFractionDigits: 4 })}`
                    : tradeType === 'sell'
                      ? `1 ${fromAsset} = $${fromPrice.toLocaleString(undefined, { maximumFractionDigits: 4 })}`
                      : `1 ${fromAsset} = ${(fromPrice / toPrice).toFixed(4)} ${selectedToken?.symbol}`
                  }
                </span>
              </div>
            </div>
          </div>
        )}

        {/* Trade Message */}
        {tradeMessage && (
          <div className={`p-2 border-2 ${
            tradeMessage.type === 'success'
              ? 'border-game-profit bg-game-profit/10'
              : 'border-game-loss bg-game-loss/10'
          }`}>
            <p className={`font-retro text-xs ${
              tradeMessage.type === 'success' ? 'text-game-profit' : 'text-game-loss'
            }`}>
              {tradeMessage.text}
            </p>
            <p className="font-retro text-xs text-game-muted mt-1 italic">
              "{tradeMessage.quote}"
            </p>
          </div>
        )}

        {/* Trade Button */}
        <RetroButton
          variant={tradeType === 'sell' ? 'default' : 'primary'}
          size="lg"
          className={`w-full ${tradeType === 'sell' ? 'bg-game-loss hover:bg-game-loss/80' : ''}`}
          onClick={handleTradeClick}
          disabled={
            !amount ||
            parseFloat(amount) <= 0 ||
            isTrading ||
            (tradeType === 'buy' && !selectedToken) ||
            !currentFromAsset
          }
        >
          {isTrading
            ? 'EXECUTING...'
            : tradeType === 'buy'
              ? `BUY ${selectedToken?.symbol || 'TOKEN'}`
              : tradeType === 'sell'
                ? `SELL ${fromAsset} FOR USDC`
                : `SWAP ${fromAsset} → ${selectedToken?.symbol || 'TOKEN'}`
          }
        </RetroButton>

        {/* Trade size indicator */}
        {amount && parseFloat(amount) > 0 && session && (
          <div className="text-center">
            <span className={`font-mono text-xs ${
              getTradeValuePercent() >= 90 ? 'text-game-loss' :
              getTradeValuePercent() >= 50 ? 'text-yellow-400' :
              'text-game-muted'
            }`}>
              Trade size: {getTradeValuePercent().toFixed(1)}% of portfolio
              {getTradeValuePercent() >= 50 && ' ⚠️'}
            </span>
          </div>
        )}
      </div>

      {/* Confirmation Modal */}
      {pendingTrade && (
        <TradeConfirmModal
          isOpen={showConfirmModal}
          tradeType={pendingTrade.type}
          fromSymbol={pendingTrade.fromSymbol}
          toSymbol={pendingTrade.toSymbol}
          amount={pendingTrade.amount}
          estimatedOutput={pendingTrade.estimatedOutput}
          portfolioValue={session?.player.portfolio.totalValue || 0}
          portfolioPercent={getTradeValuePercent()}
          onConfirm={handleConfirmTrade}
          onCancel={handleCancelTrade}
        />
      )}
    </RetroWindow>
  )
}
